<template>
    <chart-template :title="$t('Available Time Breakdown')">
        <div class="row charts">
            <v-chart
                v-if="comparisonReport"
                class="col"
                :option="comparisonReportOptions"
                autoresize
            />
            <v-chart class="col" :option="reportOptions" autoresize />
        </div>
    </chart-template>
</template>

<script>
import { formatDate } from '@utils/dateRangeFormatter.js';
import { colors } from 'quasar';
import ChartTemplate from './ChartTemplate.vue';
const { getPaletteColor } = colors;
import { mapState } from 'pinia';
import { useAuthStore } from '@stores/auth';

import { PieChart } from 'echarts/charts';
import {
    LegendComponent,
    TitleComponent,
    TooltipComponent
} from 'echarts/components';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import VChart from 'vue-echarts';

use([
    CanvasRenderer,
    PieChart,
    TitleComponent,
    TooltipComponent,
    LegendComponent
]);

export default {
    name: 'KPIAvailabilityChart',
    components: { VChart, ChartTemplate },
    props: {
        report: {
            type: Object,
            required: true
        },
        comparisonReport: {
            type: Object,
            required: false
        }
    },
    computed: {
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        reportOptions() {
            const legends = this.getTitle(
                this.report,
                this.$t('Current Period')
            );
            return this.getChartOptions(this.report, legends);
        },
        comparisonReportOptions() {
            const legends = this.getTitle(
                this.comparisonReport,
                this.$t('Comparison Period')
            );
            return this.getChartOptions(this.comparisonReport, legends);
        }
    },
    methods: {
        /**
         * Get chart title if comparison report is available.
         * @param {Object} report
         * @param {String} chartTitle
         * @returns {Object} object containing chart title and subtitle
         */
        getTitle(report, chartTitle) {
            let title = null;
            let subtitle = null;
            if (this.comparisonReport) {
                title = chartTitle;
                subtitle = formatDate(
                    report.dateInterval.start,
                    report.dateInterval.end,
                    this.userLocale
                );
            }
            return { title: title, subtitle: subtitle };
        },
        /**
         * Get the chart definition for the echart component.
         * @param {Object} report object
         * @param {Object} legends object containing chart title and subtitle
         * @returns {Object} full chart options object
         */
        getChartOptions(report, legends) {
            return {
                title: {
                    text: legends.title,
                    textStyle: {
                        fontSize: 14
                    },
                    subtext: legends.subtitle,
                    subtextStyle: {
                        fontSize: 14
                    },
                    bottom: 0,
                    left: 'center'
                },
                tooltip: {
                    trigger: 'item',
                    formatter: `{b}: <b>{c} ${this.$t('min')} ({d}%)</b>`,
                    borderColor: '#fff' // remove tooltip border
                },
                series: [
                    {
                        name: this.$t('Available Time Breakdown'),
                        type: 'pie',
                        radius: '50%',
                        data: [
                            {
                                value: (
                                    report.totals.availability.running / 60
                                ).toFixed(2),
                                name: this.$t('Running'),
                                label: {
                                    formatter: `{b} \n {c} ${this.$t('min')}\n ({d}%)`,
                                    fontSize: 13
                                }
                            },
                            {
                                value: (
                                    report.totals.availability
                                        .availabilityDowntime / 60
                                ).toFixed(2),
                                name: this.$t('Availability loss'),
                                label: {
                                    formatter: `{b} \n {c} ${this.$t('min')}\n ({d}%)`,
                                    fontSize: 13
                                }
                            },
                            {
                                value: (
                                    report.totals.availability
                                        .performanceDowntime / 60
                                ).toFixed(2),
                                name: this.$t('Performance loss'),
                                label: {
                                    formatter: `{b} \n {c} ${this.$t('min')}\n ({d}%)`,
                                    fontSize: 13
                                }
                            }
                        ],
                        emphasis: {
                            itemStyle: {
                                shadowBlur: 25,
                                shadowOffsetX: 0,
                                shadowColor: 'rgba(0, 0, 0, 0.5)'
                            }
                        }
                    }
                ],
                // order: running, availability downtime, performance downtime
                color: [
                    getPaletteColor('light-green'),
                    getPaletteColor('light-yellow'),
                    getPaletteColor('dark-yellow')
                ]
            };
        }
    }
};
</script>

<style lang="scss" scoped>
@import './dashboard.scss';
.charts {
    padding-bottom: 10px;
    height: 410px !important;
    max-width: 100%;
}
</style>
